// iter-111B — Helpers + tiny <ResponsiveImg> component that maps an
// upload URL like /api/uploads/<stem>.<ext> to the sibling WebP
// variants generated by the backend (`<stem>.webp` +
// `<stem>_thumb.webp`).
//
// Two ways to use it:
//
//   1. Inline props (for tight grids where a wrapper would break
//      flexbox / aspect-ratio):
//        import { responsiveImgProps } from '../lib/responsiveImage';
//        <img {...responsiveImgProps(area.before_image, '480px')} alt="…" />
//      The original URL stays as `src` so legacy uploads without
//      sibling .webp files never show a broken image. Modern browsers
//      pick the smaller WebP from `srcSet` when available.
//
//   2. <picture> wrapper (for hero/full-page images where the browser
//      MUST fall back to the original on missing variants):
//        import ResponsiveImg from '../lib/responsiveImage';
//        <ResponsiveImg src={url} alt="…" className="…" />
//      Renders a <picture> with a <source type="image/webp"> + an
//      <img> fallback, which is the canonical W3C pattern.
//
// Skipped for SVG and GIF (vector / animated — already lightweight).

import { isImageUrl, mediaExt } from './media';

const SKIP_EXTS = new Set(['svg', 'gif']);

function stemAndPath(src) {
  const m = String(src).match(/(\/api\/uploads\/)([^/?#]+)\.(\w+)(\?.*)?$/i);
  if (!m) return null;
  return { prefix: m[1], stem: m[2], ext: m[3].toLowerCase(), suffix: m[4] || '' };
}

function deriveWebp(src) {
  const parts = stemAndPath(src);
  if (!parts) return null;
  return {
    full: `${parts.prefix}${parts.stem}.webp${parts.suffix}`,
    thumb: `${parts.prefix}${parts.stem}_thumb.webp${parts.suffix}`,
  };
}

function canDeriveVariants(src) {
  if (!src) return false;
  if (!isImageUrl(src)) return false;
  const ext = mediaExt(src);
  if (SKIP_EXTS.has(ext)) return false;
  if (/^(data:|blob:)/i.test(src)) return false;
  if (/^https?:\/\//i.test(src) && !src.includes('/api/uploads/')) return false;
  return Boolean(stemAndPath(src));
}

/**
 * Build the props (src + srcSet + sizes + loading hints) for a
 * responsive <img> tag. Returns `{src}` only when variants cannot be
 * derived (external host, SVG, GIF, data:, blob:, etc.).
 *
 * IMPORTANT: keeps `src` as the ORIGINAL upload URL so legacy uploads
 * without sibling .webp files never appear broken. Browsers pick the
 * smaller WebP from `srcSet` when both are valid.
 */
export function responsiveImgProps(src, sizes) {
  if (!canDeriveVariants(src)) return { src };
  const webp = deriveWebp(src);
  if (!webp) return { src };
  return {
    src,
    srcSet: `${webp.thumb} 480w, ${webp.full} 1920w`,
    sizes: sizes || '(max-width: 640px) 100vw, 480px',
    loading: 'lazy',
    decoding: 'async',
  };
}

/**
 * <picture> wrapper that ALSO advertises a webp `<source>` so
 * browsers that fail to fetch a srcset candidate fall back cleanly
 * to the original `<img>`. Use this when the canonical W3C fallback
 * is required (e.g. hero images, lightbox enlargements).
 */
export default function ResponsiveImg({ src, alt = '', sizes, className, style, onClick, ...rest }) {
  if (!canDeriveVariants(src)) {
    return (
      <img
        src={src}
        alt={alt}
        className={className}
        style={style}
        onClick={onClick}
        loading="lazy"
        decoding="async"
        {...rest}
      />
    );
  }
  const webp = deriveWebp(src);
  const srcSet = `${webp.thumb} 480w, ${webp.full} 1920w`;
  return (
    <picture>
      <source srcSet={srcSet} sizes={sizes || '(max-width: 640px) 100vw, 480px'} type="image/webp" />
      <img
        src={src}
        alt={alt}
        className={className}
        style={style}
        onClick={onClick}
        loading="lazy"
        decoding="async"
        {...rest}
      />
    </picture>
  );
}
